import Vue from "vue";
import { newLogger } from "@/utils/util";
import network from "../../../utils/network";
import {
  CONDITIONAL_FIELD_OPERATOR,
  FLOW_QUESTION_FIELD_TYPE,
  SIMPLE_FIELD_TYPE,
  SPECIAL_FIELD_TYPE,
} from "../../../utils/const";

let logger = newLogger("ApplicationStore");

const urls = {
  myApplication: `/merchant/my-application`,
  myApplicationVisible: `/merchant/my-application-visible`,
  getSigningDocuments: `/merchant/my-application/documents-for-signing`,
  signatureErrorFound: `/merchant/application-signature/error-found`,

  getPreviousDocuments: (promptId) =>
    `/merchant/my-application/document/${promptId}`,
  pollStatus: `/merchant/my-application/status`, // GET
  getMyFlow: `/merchant/my-application/flow`, // GET
  getFlowForApplication: (applicationId) =>
    `/merchant/application/details/flow/${applicationId}`, // GET
  updateData: `/merchant/my-application/data`, // PATCH
  updatePosition: `/merchant/my-application/update-position`, // PATCH
  finish: `/merchant/my-application/finish`, // POST
  uploadFile: `/merchant/my-application/document`, // PATCH

  startKyc: `/merchant/my-application/identification/start`, // POST
  startSign: `/merchant/my-application/document-signing/start`, // POST
};

/**
 * Merchant Application store
 */

const initState = () => {
  return {
    application: {},
    flow: [],
    flowConfig: {},
    applicationData: {},
    signatureProcess: {},
    comnicaRecord: {},
    loaded: false,
    kycMobile: false,
    signMobile: false,
    myApplicationVisible: false,
  };
};

const actions = {
  loadEverything: async ({ dispatch, commit }) => {
    await dispatch("fetchApplication");
    await dispatch("fetchMyFlow");
    commit("loaded");
    return true;
  },
  fetchSignatureProcess: async ({ commit }) => {
    try {
      const axios = await network.connection();
      let { data } = await axios.get(urls.getSigningDocuments);
      commit("saveSignatureProcess", data);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchPreviousDocuments: async ({}, promptId) => {
    try {
      const axios = await network.connection();
      let { data } = await axios.get(urls.getPreviousDocuments(promptId));
      return data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchApplication: async ({ commit }) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.get(urls.myApplication);

      commit("saveApplication", data);

      return data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchMyApplicationVisible: async ({ commit }) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.get(urls.myApplicationVisible);

      commit("saveMyApplicationVisible", data);

      return data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchMyFlow: async ({ commit }) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.get(urls.getMyFlow);

      commit("saveFlow", data);

      return data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchFlowForApplication: async ({ commit }, applicationId) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.get(
        urls.getFlowForApplication(applicationId)
      );

      commit("saveFlow", data);

      return data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  pollStatus: async ({ commit }) => {
    try {
      const axios = await network.connection();
      try {
        const { data } = await axios.get(urls.pollStatus);
        commit("updateStatus", data);
      } catch (ex) {
        commit("updateStatus", {
          currentStatus: null,
          currentStatusResult: null,
          currentPosition: null,
        });
      }
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  saveDynamicData: async ({ commit }, { key, value }) => {
    try {
      commit("updateDynamicData", { key, value });
      const axios = await network.connection();
      await axios.patch(urls.updateData, {
        key,
        value,
      });
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  setDynamicData: async ({ commit }, dynamicDataString) => {
    commit("setDynamicData", JSON.parse(dynamicDataString));
  },
  // close: async ({ commit }) => {
  //   // todo local update
  //   try {
  //     const axios = await network.connection();
  //     await axios.post(urls.close);
  //   } catch (err) {
  //     logger.error(err);
  //     await Promise.reject(err);
  //   }
  // },
  finish: async () => {
    try {
      const axios = await network.connection();
      await axios.post(urls.finish);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  signatureErrorFound: async ({}, request) => {
    try {
      const axios = await network.connection();
      await axios.patch(urls.signatureErrorFound, request);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  savePosition: async ({ commit }, { flowPosition, currentFlowStepIndex }) => {
    try {
      const axios = await network.connection();
      await axios.patch(urls.updatePosition, {
        flowPosition: flowPosition,
        currentFlowIndex: currentFlowStepIndex,
      });

      commit("updatePosition", {
        flowPosition,
        currentFlowStepIndex,
      });
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  setKycMobile: ({ commit }, val) => {
    commit("updateKycMobile", val);
  },
  setSignMobile: ({ commit }, val) => {
    commit("updateSignMobile", val);
  },
  startKyc: async ({ commit }) => {
    try {
      const axios = await network.connection();
      let { data } = await axios.post(urls.startKyc);

      commit("saveComnica", data);
      commit("updatePosition", {
        flowPosition: "KYC_WAITING",
        currentFlowStepIndex: false,
      });
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  startSign: async ({ commit }) => {
    try {
      const axios = await network.connection();
      let { data } = await axios.post(urls.startSign);

      commit("saveComnica", data);
      commit("updatePosition", {
        flowPosition: "SIGN_WAITING",
        currentFlowStepIndex: false,
      });
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  uploadFile: async ({}, { file, promptId, promptIdentifier }) => {
    try {
      const axios = await network.connection();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("promptId", promptId);
      formData.append("promptIdentifier", promptIdentifier);
      await axios.patch(urls.uploadFile, formData);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
};

const mutations = {
  saveApplication: (state, data) => {
    Vue.set(state, "applicationData", JSON.parse(data.dynamicData));
    Vue.set(state, "application", data);
    if (data.projectRecords && data.projectRecords.length)
      if (data.projectRecords.length === 1) {
        Vue.set(state, "comnicaRecord", data.projectRecords[0]);
      } else {
        let records = data.projectRecords.sort(
          (a, b) => a.recordId - b.recordId
        );
        Vue.set(state, "comnicaRecord", records[records.length - 1]);
      }
  },
  saveMyApplicationVisible: (state, data) => {
    Vue.set(state, "myApplicationVisible", data);
  },
  saveSignatureProcess: (state, data) => {
    Vue.set(state, "signatureProcess", data);
  },
  saveFlow: (state, flow) => {
    if (state.flow.length) {
      Vue.set(state, "flow", []);
    }
    let steps = flow.steps.sort((a, b) => {
      return a.rowIndex - b.rowIndex;
    });
    for (let s in steps) {
      steps[s].questions = steps[s].questions.filter((q) => !q.hidden);
      state.flow.push(steps[s]);
    }

    Vue.set(state, "flowConfig", flow);
    Vue.set(state.flowConfig, "steps", null);
  },
  updateDynamicData: (state, { key, value }) => {
    Vue.set(state.applicationData, key, value);
  },
  setDynamicData: (state, dynamicData) => {
    Vue.set(state, "applicationData", dynamicData);
  },
  updatePosition: (state, { flowPosition, currentFlowStepIndex }) => {
    Vue.set(state.application, "currentPosition", flowPosition);
    if (currentFlowStepIndex || currentFlowStepIndex === 0)
      Vue.set(state.application, "currentFlowIndex", currentFlowStepIndex);
  },
  loaded: (state) => {
    Vue.set(state, "loaded", true);
  },
  updateKycMobile: (state, val) => {
    Vue.set(state, "kycMobile", val);
  },
  updateSignMobile: (state, val) => {
    Vue.set(state, "signMobile", val);
  },
  saveComnica: (state, data) => {
    Vue.set(state, "comnicaRecord", data);
  },
  updateStatus: (state, data) => {
    Vue.set(state.application, "currentStatus", data.currentStatus);
    Vue.set(state.application, "currentStatusResult", data.currentStatusResult);
    Vue.set(state.application, "currentPosition", data.currentPosition);
  },
};

const getters = {
  getActiveApplication: (state) => {
    return state.application;
  },
  isApplicationButtonVisible: (state) => {
    return state.myApplicationVisible;
  },
  loaded: (state) => {
    return state.loaded;
  },
  flow: (state) => {
    return state.flow;
  },
  flowConfig: (state) => {
    return state.flowConfig;
  },
  getFlowQuestionAnswer: (state) => (identifier) => {
    return state.applicationData[identifier];
  },
  isKycMobile: (state) => {
    return state.kycMobile;
  },
  getSignatureProcess: (state) => {
    return state.signatureProcess;
  },
  isSignMobile: (state) => {
    return state.signMobile;
  },
  applicationStatus: (state) => {
    return state.application.currentStatus;
  },
  applicationStatusResult: (state) => {
    return state.application.currentStatusResult;
  },
  canContinue: (state, getters, rootState) => (step) => {
    for (let q in state.flow[step].questions) {
      let question = state.flow[step].questions[q];
      let field;
      switch (question.fieldType) {
        case FLOW_QUESTION_FIELD_TYPE.SIMPLE_FIELD:
          field = rootState.simpleFieldStore.fieldData[question.fieldId];
          break;
        case FLOW_QUESTION_FIELD_TYPE.STATEMENT_PROMPT:
          field =
            rootState.statementPromptStore.statementData[question.fieldId];
          break;
        case FLOW_QUESTION_FIELD_TYPE.DOCUMENT_PROMPT:
          field = rootState.documentPromptStore.documentData[question.fieldId];
          break;
        case FLOW_QUESTION_FIELD_TYPE.SPECIAL_FIELD:
          field = rootState.specialFieldStore.speciaFieldData[question.fieldId];
          break;
        case FLOW_QUESTION_FIELD_TYPE.READONLY_FIELD:
          continue;
      }
      if (question.isRequired) {
        let needAnswer;
        if (question.isConditional) {
          for (const condition of question.conditions) {
            let index = question.conditions.indexOf(condition);

            let dependency =
              rootState.simpleFieldStore.fieldData[
                condition.conditionalFieldId
              ];

            let evaluate;

            let answer = state.applicationData[dependency.identifier];
            if (dependency.type === SIMPLE_FIELD_TYPE.CHECKBOX) {
              if (answer) {
                let answerObject = JSON.parse(answer);
                evaluate = answerObject.includes(
                  condition.conditionalFieldAnswer
                );
              } else {
                evaluate = false;
              }
            } else {
              evaluate = answer === condition.conditionalFieldAnswer;
            }

            if (index === 0) {
              needAnswer = evaluate;
            } else {
              let operator =
                question.conditions[index - 1].conditionalFieldOperator;
              if (CONDITIONAL_FIELD_OPERATOR.ENUM.AND === operator) {
                needAnswer = needAnswer && evaluate;
              } else if (CONDITIONAL_FIELD_OPERATOR.ENUM.OR === operator) {
                needAnswer = needAnswer || evaluate;
              }
            }
          }

          if (needAnswer) {
            if (!state.applicationData[field.identifier]) {
              console.warn("Conditionally required failed!");
              return false;
            }
          }
        } else {
          if (question.fieldType === FLOW_QUESTION_FIELD_TYPE.SPECIAL_FIELD) {
            if (question.fieldSubtype === SPECIAL_FIELD_TYPE.SELF_ONBOARDING) {
              if (!rootState.onboarding.onboardingDataValidity) {
                console.warn("Required failed!");
                return false;
              }
            }
          } else {
            if (!state.applicationData[field.identifier]) {
              console.warn("Required failed!");
              return false;
            }
          }
        }
      }
      if (question.isHardKo) {
        if (state.applicationData[field.identifier] === question.koAnswer) {
          console.warn("KO failed!");
          return false;
        }
      }
      if (field && field.type === SIMPLE_FIELD_TYPE.INPUT && field.validation) {
        let value = state.applicationData[field.identifier];

        if (value === undefined) {
          value = "";
        }

        const regex = new RegExp(field.validation);
        if (!regex.test(value)) {
          console.warn("Regex validation failed!");
          return false;
        }
      }
    }
    return true;
  },
  comnicaUrl: (state) => {
    return state.comnicaRecord.url;
  },
};

export default {
  namespaced: true,
  state: initState(),
  mutations: mutations,
  actions: actions,
  getters: getters,
};
